// How much of a thing — the one place the app decides what a quantity IS.
//
// The store keeps three fields per ingredient and they are not alternatives to
// each other, they are a fallback chain:
//
//   amount    a real number, or nothing.  10, 0.5, 2
//   unit      what that number counts.    "kg", "head", "tbsp", "handfuls"
//   quantity  the cook's own words, kept verbatim, used only when there is no
//             amount. "a finger of space", "enough to cover"
//
// The reason for the split is that half of a real recipe is measured and half
// of it is not, and an app that forces the second half through a number field
// throws away the only part that sounds like a person. So: a number where a
// number exists, and the sentence otherwise — never a 0 standing in for
// "enough to cover".

/** Vulgar fractions someone might actually type or paste, as real numbers. */
const VULGAR = {
  '¼': 1 / 4, '½': 1 / 2, '¾': 3 / 4,
  '⅐': 1 / 7, '⅑': 1 / 9, '⅒': 1 / 10,
  '⅓': 1 / 3, '⅔': 2 / 3,
  '⅕': 1 / 5, '⅖': 2 / 5, '⅗': 3 / 5, '⅘': 4 / 5,
  '⅙': 1 / 6, '⅚': 5 / 6,
  '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8,
};

const GLYPHS = Object.keys(VULGAR).join('');

/* A leading numeric run, most specific first. Order is load-bearing: "1 1/2"
 * must beat "1", and "0.5" must beat "0". */
const LEADING = new RegExp(
  '^(' +
    '\\d+\\s+\\d+\\s*/\\s*\\d+' +   // 1 1/2
    '|\\d+\\s*[' + GLYPHS + ']' +    // 1½
    '|\\d+\\s*/\\s*\\d+' +          // 1/2
    '|[' + GLYPHS + ']' +            // ½
    '|\\d*\\.\\d+' +                // .5 or 1.5
    '|\\d+' +                        // 10
  ')'
);

/** Fractions worth writing as a fraction on a recipe card. Anything else stays
 *  a decimal — "1.33 cups" is honest, "1 1/3 cups" would be a rounding nobody
 *  asked for.
 *
 *  Written as ASCII, not as the vulgar glyphs (½ ⅓ ¾). parseQuantity still
 *  READS a pasted glyph, because someone will paste one; it is only what the
 *  app WRITES that is spelled out. A glyph is a font's opinion — it goes
 *  missing in a fallback face, it does not survive a copy into a text message
 *  to your sister, and it sets smaller than the digits beside it in a column
 *  whose whole point is that the figures line up. */
const NICE = [
  [1 / 2, '1/2'], [1 / 3, '1/3'], [2 / 3, '2/3'],
  [1 / 4, '1/4'], [3 / 4, '3/4'],
  [1 / 8, '1/8'], [3 / 8, '3/8'], [5 / 8, '5/8'], [7 / 8, '7/8'],
];

/** Kill the float noise that 1/3 and any multiplication of it leave behind. */
const tidy = (n) => Math.round(n * 1e6) / 1e6;

/** A number as a cook would write it: 10, 1 1/2, 3/4, 1.33. */
function formatAmount(n) {
  const whole = Math.floor(n);
  const frac = n - whole;
  if (frac < 1e-6) return String(whole);
  for (const [value, text] of NICE) {
    if (Math.abs(frac - value) < 1e-3) return whole ? `${whole} ${text}` : text;
  }
  return String(Math.round(n * 100) / 100);
}

/**
 * What to print in the quantity column.
 * "10 kg" | "1/2 cup" | "2 handfuls" | "a finger of space" | ""
 *
 * The amount wins when there is one; the cook's words are the fallback, not a
 * second opinion. Zero counts as no amount — nobody means "0 kg", they mean
 * they never gave a number.
 *
 * @param {{amount?: number|string|null, unit?: string|null, quantity?: string|null}} ing
 * @returns {string}
 */
export function formatQuantity(ing) {
  if (!ing) return '';
  const amount = Number(ing.amount);
  if (Number.isFinite(amount) && amount !== 0) {
    const unit = String(ing.unit ?? '').trim();
    const n = formatAmount(amount);
    return unit ? `${n} ${unit}` : n;
  }
  return String(ing.quantity ?? '').trim();
}

/**
 * Split what a person typed into one field into a real number plus a unit,
 * when it can, and leave it alone when it can't.
 *
 * Handles: "10", "10 kg", "10kg", "0.5", ".5", "1/2 cup", "½ cup",
 *          "1 1/2 cups", "1½ cups", "2 handfuls", "2 handfuls of".
 *
 * Kept verbatim, on purpose — in every one of these the amount is genuinely not
 * one number, and half-reading it would be worse than not reading it:
 *          no leading number at all  "a finger of space", "enough to cover"
 *          a range                   "1-2 tbsp", "1 to 2 tbsp"
 *          a decimal comma           "1,5 kg" — ambiguous against a thousands
 *                                    separator, so it stays as typed rather
 *                                    than becoming 1 with a unit of ",5 kg"
 *          a second number           "2 1 kg"
 *          zero, or a divide by zero "0", "1/0"
 *
 * Also NOT attempted: splitting the ITEM out of the unit. The store has a
 * separate `item` field and the caller owns that split, so "10 kg Roma
 * tomatoes" typed into the quantity box yields a unit of "kg Roma tomatoes".
 *
 * @param {string} text
 * @returns {{amount: number|null, unit: string, quantity: string}}
 */
export function parseQuantity(text) {
  const raw = String(text ?? '').trim();
  /* Nothing typed is not free text — it is nothing. */
  if (!raw) return { amount: null, unit: '', quantity: '' };

  /* Whatever we could not read stays exactly as she said it. */
  const verbatim = () => ({ amount: null, unit: '', quantity: raw });

  const head = raw.match(LEADING);
  if (!head) return verbatim();

  const amount = toNumber(head[0]);
  if (amount === null || !Number.isFinite(amount) || amount <= 0) return verbatim();

  let rest = raw.slice(head[0].length).trim();

  /* "1-2 tbsp", "1 to 2 tbsp", "1 or 2" — a range is not an amount. Keeping
     the first number and calling "-2 tbsp" the unit would be worse than not
     parsing at all. */
  if (/^(?:[-–—]|to\b|or\b)/i.test(rest)) return verbatim();

  /* No unit begins with a digit, a comma or a point. If one appears to, we
     mis-split the number — "1,5 kg" and "2 1 kg" both land here — so back off
     and keep the whole string rather than inventing a unit out of the rest of
     somebody's number. */
  if (/^[\d.,]/.test(rest)) return verbatim();

  /* "2 handfuls of" — the "of" belongs to the item, not the unit. */
  rest = rest.replace(/\s+of$/i, '').trim();

  return { amount: tidy(amount), unit: rest, quantity: '' };
}

/** The leading run, as a number. null when it is not one. */
function toNumber(head) {
  const s = head.replace(/\s+/g, ' ').trim();

  let m = s.match(/^(\d+) (\d+)\s*\/\s*(\d+)$/);          // 1 1/2
  if (m) return Number(m[3]) === 0 ? null : Number(m[1]) + Number(m[2]) / Number(m[3]);

  m = s.match(new RegExp('^(\\d+)\\s*([' + GLYPHS + '])$'));  // 1½
  if (m) return Number(m[1]) + VULGAR[m[2]];

  m = s.match(/^(\d+)\s*\/\s*(\d+)$/);                     // 1/2
  if (m) return Number(m[2]) === 0 ? null : Number(m[1]) / Number(m[2]);

  if (VULGAR[s] != null) return VULGAR[s];                 // ½

  const n = Number(s);                                     // 10, 0.5, .5
  return Number.isFinite(n) ? n : null;
}

/**
 * Double the sugo. Multiplies a measured amount and returns a NEW row —
 * scaling must never mutate what is in the store.
 *
 * A free-text quantity is left exactly alone: two handfuls times three is not
 * six handfuls of anything you could hold, and "a finger of space" does not
 * scale at all. Returning it untouched is the honest answer.
 *
 * @param {object} ing an ingredient row
 * @param {number} factor
 * @returns {object} a copy of the row
 */
export function scaleQuantity(ing, factor) {
  if (!ing) return ing;
  const f = Number(factor);
  const amount = Number(ing.amount);
  if (!Number.isFinite(f) || f <= 0) return { ...ing };
  if (!Number.isFinite(amount) || amount === 0) return { ...ing };
  return { ...ing, amount: tidy(amount * f) };
}
